import { useEffect, useState } from "react";
import { Check, Trash2, X, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { Stars } from "@/components/Reviews";

const TONE = { pending: "bg-[#F2C46D] text-[#1C2421]", approved: "bg-[#DCE8DF] text-[#1E3A2B]", rejected: "bg-[#EFE9E1] text-[#5C6B63]" };

function ReviewRow({ r, reload }) {
  const [reply, setReply] = useState(r.reply || "");
  const patch = async (body, msg) => {
    try { await api.patch(`/admin/reviews/${r.id}`, body); toast.success(msg); reload(); } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete the review by ${r.name}?`)) return;
    await api.delete(`/admin/reviews/${r.id}`); toast.success("Review deleted"); reload();
  };
  return (
    <div className="p-4 grid gap-3 md:grid-cols-[1fr_260px]" data-testid={`admin-review-${r.id}`}>
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <Stars value={r.rating} size={14}/>
          <span className={`text-[11px] px-2 py-0.5 rounded-full ${TONE[r.status]}`} data-testid={`admin-review-status-${r.id}`}>{r.status}</span>
          {r.verified && <span className="text-[11px] inline-flex items-center gap-1 text-[#1E3A2B]"><BadgeCheck size={12}/> verified {r.booking_ref}</span>}
          {r.is_sample && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#F2C46D]/50">sample, delete when you have real reviews</span>}
        </div>
        <div className="font-medium mt-2">{r.title || "(no title)"}</div>
        <p className="text-sm text-[#3B4A42] mt-1">{r.text}</p>
        <div className="text-xs text-[#5C6B63] mt-2">{r.name}{r.location && ` · ${r.location}`}{r.email && ` · ${r.email}`} · {new Date(r.created_at).toLocaleDateString("en-IN")}</div>
      </div>
      <div className="space-y-2">
        <div className="flex gap-2">
          {r.status !== "approved" && <button onClick={() => patch({ status: "approved" }, "Approved, now on the Home page")} className="jt-pill-primary text-xs" data-testid={`admin-review-approve-${r.id}`}><Check size={13}/> Approve</button>}
          {r.status !== "rejected" && <button onClick={() => patch({ status: "rejected" }, "Hidden")} className="jt-pill-outline text-xs" data-testid={`admin-review-reject-${r.id}`}><X size={13}/> {r.status === "approved" ? "Hide" : "Reject"}</button>}
          <button onClick={remove} className="p-2" aria-label="Delete" data-testid={`admin-review-delete-${r.id}`}><Trash2 size={14} className="text-[#C86D51]"/></button>
        </div>
        <textarea rows={2} className="w-full rounded-md border border-[#E2DACF] p-2 text-xs" placeholder="Public reply (optional)" value={reply} onChange={(e) => setReply(e.target.value)} data-testid={`admin-review-reply-${r.id}`}/>
        {reply !== (r.reply || "") && <button onClick={() => patch({ reply }, "Reply saved")} className="text-xs underline" data-testid={`admin-review-reply-save-${r.id}`}>Save reply</button>}
      </div>
    </div>
  );
}

export default function ReviewsManager() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/admin/reviews").then((r) => setRows(r.data)).catch((e) => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []);
  const shown = filter === "all" ? rows : rows.filter((r) => r.status === filter);
  const pending = rows.filter((r) => r.status === "pending").length;
  return (
    <div className="space-y-4" data-testid="reviews-admin">
      <p className="text-sm text-[#5C6B63]">Guests send reviews from the Home page. Approved reviews appear there and are added to Google's review data. Sample reviews are labelled on the site and left out of Google data.</p>
      <div className="flex gap-2">
        {["all", "pending", "approved", "rejected"].map((k) => (
          <button key={k} onClick={() => setFilter(k)} className={`px-3 py-1.5 rounded-full text-xs capitalize ${filter === k ? "bg-[#1E3A2B] text-white" : "bg-white ring-1 ring-black/5"}`} data-testid={`reviews-filter-${k}`}>
            {k}{k === "pending" && pending ? ` (${pending})` : ""}
          </button>
        ))}
      </div>
      <div className="bg-white ring-1 ring-black/5 rounded-2xl divide-y divide-[#EFE9E1]">
        {shown.length === 0 && <p className="p-4 text-sm text-[#5C6B63]">No reviews here.</p>}
        {shown.map((r) => <ReviewRow key={r.id + (r.updated_at || "")} r={r} reload={load}/>)}
      </div>
    </div>
  );
}
